import type { CSSProperties } from "react";
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  Calendar,
  CaretDown,
  CaretUp,
  Check,
  CheckCircle,
  ClockCounterClockwise,
  Copy,
  CursorClick,
  DotsThree,
  DownloadSimple,
  FilePdf,
  FloppyDisk,
  FolderOpen,
  Info,
  List,
  MagnifyingGlass,
  Minus,
  Moon,
  Plus,
  SignOut,
  Sliders,
  SpinnerGap,
  SquaresFour,
  Sun,
  Trash,
  Warning,
  WarningCircle,
  X,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Warum eine feste, KLEINE Registry (T34: „Phosphor-Icons mit hartem
// Namensdeckel ≤ 30").
//
// Jedes Phosphor-Modul enthält alle sechs Strichstärken als eingebettete
// React-Elemente in EINEM `new Map([...])`-Literal. Weil das ein atomarer
// Ausdruck ist, kann der Bündler die ungenutzten Stärken nicht wegschütteln:
// rund 3,3 KB roh je Symbol, gleich wie viele Stärken davon wirklich
// gezeichnet werden. In webgis waren 175 Symbole rund 576 KB im
// modulepreload des Eingangs — dort musste die Registry geteilt und die
// zweite Hälfte nachgeladen werden.
//
// Diese Liste ist mit 30 Namen GENAU AM DECKEL. Wer eines ergänzt, nimmt
// eines heraus — oder hebt den Deckel als eigene Entscheidung, nicht als
// Beigabe. Oberhalb von etwa 60 lohnt sich der geteilte Weg aus
// /opt/webgis/frontend/src/components/ui/Icon.tsx wieder.
//
// Die 14 Piktogramme DES BLATTS sind hier bewusst nicht dabei: sie sind
// handgezeichnete Pfade in packages/kern/src/icons.ts und Teil des
// Druckerzeugnisses (SPEC §3.7). Phosphor zeichnet nur die Bedienoberfläche.
// ---------------------------------------------------------------------------
const REGISTRY = {
  // Kopfleiste und Geschichte
  "arrow-counter-clockwise": ArrowCounterClockwise, // Rückgängig
  "arrow-clockwise": ArrowClockwise, // Wiederholen
  "clock-counter-clockwise": ClockCounterClockwise, // Stände
  "folder-open": FolderOpen, // Öffnen (JSON)
  "floppy-disk": FloppyDisk, // Speichern (JSON)
  "download-simple": DownloadSimple, // Exportieren
  "file-pdf": FilePdf, // PDF importieren / Quelle öffnen
  sun: Sun,
  moon: Moon,
  "sign-out": SignOut,
  "dots-three": DotsThree, // Zeilenmenü, Reiter „Mehr"

  // Am Balken und an der Spur
  plus: Plus,
  minus: Minus,
  trash: Trash,
  copy: Copy, // Duplizieren — und „Meldung kopieren" im Toaster
  "caret-up": CaretUp, // Spur nach oben
  "caret-down": CaretDown, // Spur nach unten — und das Auswahlfeld (primitives.tsx)
  x: X, // Modal schließen, Spur löschen
  check: Check, // Auswahlfeld, Toaster
  "magnifying-glass": MagnifyingGlass, // Suche in Liste und Auswahlfeld

  // Reiter und leere Zustände
  "squares-four": SquaresFour, // Reiter Blatt
  list: List, // Reiter Einträge
  sliders: Sliders, // Reiter Einstellungen
  "cursor-click": CursorClick, // „Kein Eintrag ausgewählt."
  calendar: Calendar, // „Noch kein Plan angelegt."

  // Zustände und Meldungen
  warning: Warning,
  "warning-circle": WarningCircle,
  "check-circle": CheckCircle,
  info: Info,
  "spinner-gap": SpinnerGap,
} satisfies Record<string, PhosphorIcon>;

/** Alle Namen, die <Icon name="…"> kennt — für Aufrufer, die eine Auswahl anbieten. */
export type IconName = keyof typeof REGISTRY;
export const ICON_NAMEN = Object.keys(REGISTRY) as IconName[];

export interface IconProps {
  name: string;
  /** Strichstärke; Vorgabe "regular". Kräftige Stärken sind Ausnahmen
   *  (Meldungssymbol „fill", Häkchen „bold") — in der Fläche bleibt regular. */
  weight?: IconWeight;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** wird zum barrierefreien Namen; ohne ihn ist das Symbol reine Zier
   *  (aria-hidden) und die Vorlesehilfe überspringt es. */
  title?: string;
}

export function Icon({ name, weight = "regular", size = 18, className = "", style = {}, title }: IconProps) {
  // Die Registry trägt LITERALE Schlüssel (satisfies statt Typannotation), damit
  // IconName oben eine echte Namensliste ist und kein blosses `string`. Der
  // Zugriff hier nimmt aber jede Zeichenkette an — ein Aufrufer soll einen
  // Namen aus einer Datenbankzeile durchreichen dürfen, ohne ihn vorher zu
  // prüfen; der Zweig darunter fängt das ab.
  const Cmp = (REGISTRY as Record<string, PhosphorIcon | undefined>)[name];

  if (!Cmp) {
    // Ein vertippter Name fällt nicht beim Übersetzen auf, sondern als leere
    // Stelle im Bild. Im Entwicklungsmodus deshalb einmal laut werden; im Bau
    // bleibt ein maßhaltiger, unsichtbarer Platzhalter, damit die Zeile nicht
    // um die Breite des fehlenden Symbols zusammenrutscht.
    if (import.meta.env.DEV) console.warn(`[Icon] Name nicht in der Registry: ${name}`);
    return <span aria-hidden style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", flex: "none" }} />;
  }

  return (
    <Cmp
      size={size}
      weight={weight}
      className={className}
      style={{ verticalAlign: "middle", flex: "none", ...style }}
      aria-hidden={title ? undefined : true}
      alt={title}
    />
  );
}
